import { RoundVideoDownload } from "./RoundVideoDownload";
import type { ExportProgress } from "@lib/roundExport/contract";
import { useMemo, useState } from "react";
import { Download, Check, RefreshCw } from "lucide-react";
import {
  Button,
  Spinner,
  Progress,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@sfc/ui";
import { trpc, trpcVanilla } from "@client/trpc";
import {
  defineAction,
  confirmQuery,
  actionKey,
  type Observation,
} from "@client/actions/defineAction";
import {
  awaitEnqueueSettled,
  getAttempt,
  dispatch,
} from "@client/actions/store";
import { useActionStatus } from "@client/actions/useAction";
import { useEffectiveUser } from "@client/hooks/use-effective-user";
import { toast } from "sonner";

type ExportStatus = Awaited<
  ReturnType<typeof trpcVanilla.getRoundExport.query>
>;

const progressLabels: Record<ExportProgress["stage"], string> = {
  "round-data": "Collecting round data",
  proposals: "Collecting proposal data",
  research: "Collecting supporting research",
  transcripts: "Collecting transcripts",
  attachments: "Gathering attachments",
  documents: "Gathering application documents",
  fingerprints: "Checking video versions",
  packaging: "Packaging your download",
};
const progressUnits: Partial<Record<ExportProgress["stage"], string>> = {
  proposals: "proposals",
  attachments: "attachments checked",
  documents: "documents checked",
  fingerprints: "versions checked",
  packaging: "files completed",
};

function observe(data: ExportStatus): Observation {
  if (!data) {
    return { status: "none" };
  }

  return {
    runId: data.runId,
    requestedAt: data.requestedAt,
    error: data.error,
    status:
      data.status === "SUCCEEDED"
        ? "succeeded"
        : data.status === "FAILED"
          ? "failed"
          : data.status === "CANCELED"
            ? "canceled"
            : "pending",
  };
}

export function RoundExportButton({
  roundId,
  label,
}: {
  roundId: string;
  label?: string;
}) {
  const { data: viewer } = useEffectiveUser();

  const access = trpc.getRoundVideoExport.useQuery(
    { roundId, clientQueryScope: viewer?.userId },
    { enabled: !!viewer, staleTime: 0 },
  );

  if (!access.data) {
    return null;
  }

  // Identity changes remount local dialog state and fence the displayed export.
  return (
    <RoundExportControl
      key={`${roundId}:${viewer?.userId}`}
      roundId={roundId}
      viewerId={viewer?.userId ?? ""}
      triggerLabel={label}
    />
  );
}

function RoundExportControl({
  roundId,
  viewerId,
  mediaOf,
  triggerLabel,
}: {
  roundId: string;
  viewerId: string;
  mediaOf?: string;
  triggerLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const utils = trpc.useUtils();
  const input = useMemo(
    () => ({ roundId, clientQueryScope: viewerId, mediaOf }),
    [roundId, viewerId, mediaOf],
  );
  const action = useMemo(
    () =>
      defineAction({
        name: "round-export",
        key: () => `${roundId}:${viewerId}:${mediaOf ?? "snapshot"}`,
        confirm: "runs",
        run: async () => {
          const result = await trpcVanilla.prepareRoundExport.mutate(input);

          if (mediaOf) {
            await utils.getRoundExport.invalidate();
          }

          return result;
        },
        identify: (result: unknown) => {
          const run = result as { runId: string };
          return { runId: run.runId };
        },
        fetch: () => utils.getRoundExport.fetch(input, { staleTime: 0 }),
        inspect: (data: unknown) => observe(data as ExportStatus),
        onError: (message: string) => toast.error(message),
      }),
    [roundId, viewerId, mediaOf, input, utils],
  );
  const pending = useActionStatus(action, input);
  const cancelAction = useMemo(
    () =>
      defineAction({
        name: "cancel-round-export",
        key: () => `${roundId}:${viewerId}:${mediaOf ?? "snapshot"}`,
        confirm: "queries",
        run: async () => {
          const key = actionKey(action, input);
          const displayedRunId = utils.getRoundExport.getData(input)?.runId;
          const readiness = await awaitEnqueueSettled(key);

          if (readiness === "still-sending") {
            throw new Error(
              "Still starting the export. Try canceling again in a moment.",
            );
          }

          const attempt = getAttempt(key);
          const latest = displayedRunId
            ? undefined
            : await utils.getRoundExport.fetch(input, { staleTime: 0 });
          const runId =
            readiness === "proceed" && attempt?.identity?.runId
              ? attempt.identity.runId
              : (displayedRunId ?? latest?.runId);

          if (!runId) {
            return;
          }

          const result = await trpcVanilla.cancelRoundExport.mutate({
            ...input,
            runId,
          });

          // Settle the original action from authoritative server evidence now,
          // rather than leaving its optimistic pending state until the next poll.
          if (
            (result.status === "CANCELED" ||
              result.status === "FAILED" ||
              result.status === "SUCCEEDED") &&
            attempt?.identity?.runId === result.id
          ) {
            dispatch(key, {
              type: "CONTROL_TERMINAL",
              attemptId: attempt.attemptId,
              terminal: result.status,
            });

            if (result.status === "SUCCEEDED") {
              const confirmation = confirmQuery(utils.getRoundExport, input);
              await confirmation.cancel();
              await confirmation.fetch();
              dispatch(key, {
                type: "DISPLAY_CONFIRMED",
                attemptId: attempt.attemptId,
              });
            }
          }
        },
        confirmWith: () => [confirmQuery(utils.getRoundExport, input)],
        onError: (message: string) => toast.error(message),
      }),
    [roundId, viewerId, mediaOf, input, utils, action],
  );
  const cancel = useActionStatus(cancelAction, input);
  const query = trpc.getRoundExport.useQuery(input, {
    enabled: open,
    staleTime: 0,
    // This observes jobs started before this browser session; the action layer
    // owns polling for attempts initiated here, including while closed.
    refetchInterval: (q) =>
      pending.isPending ||
      q.state.data?.mediaPending ||
      q.state.data?.status === "QUEUED" ||
      q.state.data?.status === "RUNNING"
        ? 3000
        : false,
  });
  const data = query.data;
  const busy =
    pending.isPending ||
    data?.status === "QUEUED" ||
    data?.status === "RUNNING";
  const ready =
    !busy && data?.status === "SUCCEEDED" && !data.needsSnapshotRefresh;
  const phases =
    data?.status === "QUEUED" || data?.status === "RUNNING" ? data.phases : [];

  async function download(kind: "package" | "media") {
    if (!data) {
      return;
    }

    try {
      const download = await trpcVanilla.getRoundExportDownload.query({
        roundId,
        runId: data.runId,
        mediaOf,
        kind,
      });
      // Server mints the URL only after rechecking current viewer permissions.
      // Direct attachment delivery avoids buffering gigabytes in the browser.
      const anchor = document.createElement("a");
      anchor.href = download.url;
      anchor.download = download.filename;
      anchor.rel = "noopener";
      anchor.click();
    } catch {
      toast.error("Could not download the export. Please try again.");
    }
  }

  return (
    <>
      <Button
        variant={mediaOf || triggerLabel ? "flat-outline" : "nav"}
        size={mediaOf || triggerLabel ? "default" : "icon"}
        onClick={() => setOpen(true)}
        className={mediaOf || triggerLabel ? undefined : "rounded-full"}
        aria-label={
          mediaOf
            ? (triggerLabel ?? "Export large attachments")
            : "Export round"
        }
        title={
          mediaOf
            ? (triggerLabel ?? "Export large attachments")
            : "Export round"
        }
      >
        {mediaOf || triggerLabel ? (
          (triggerLabel ?? "Export large attachments")
        ) : (
          <Download className="size-5" aria-hidden="true" />
        )}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {mediaOf ? "Export large attachments" : "Export round"}
            </DialogTitle>
            <DialogDescription>
              {mediaOf
                ? "A separate download of the exact files referenced by your snapshot."
                : "Round data and application attachments."}
            </DialogDescription>
          </DialogHeader>
          {!busy && !ready && !mediaOf && (
            <div className="divide-y divide-border-subtle body-md">
              <div className="py-3">
                <strong>Round data</strong>
                <p className="text-text-secondary">
                  Applications, notes, evaluations, ratings, weights and saved
                  funding state.
                </p>
              </div>
              <div className="py-3">
                <strong>Research and supporting material</strong>
                <p className="text-text-secondary">
                  Deep research, citations, AI summaries, questions and
                  transcripts. Documents linked in applications are captured
                  where available; inaccessible documents are listed as missing.
                  Research citations remain links.
                </p>
              </div>
              <div className="py-3">
                <strong>Round videos</strong>
                <p className="text-text-secondary">
                  Downloaded separately from an archive prepared by an admin.
                  All captured application attachments are included in the round
                  ZIP.
                </p>
              </div>
            </div>
          )}
          <div aria-live="polite" className="body-md">
            {query.isError && (
              <p className="text-destructive">
                Could not check your export. Close and reopen to retry.
              </p>
            )}
            {busy && (
              <div className="space-y-3">
                {(
                  Object.entries(progressLabels) as [
                    ExportProgress["stage"],
                    string,
                  ][]
                )
                  .filter(([stage]) => !mediaOf || stage === "packaging")
                  .map(([stage, label]) => {
                    const phase = phases.find((item) => item.stage === stage);
                    const copiedAllItems =
                      phase?.total != null && phase.completed === phase.total;
                    // Copying files is not the end of packaging: the remaining
                    // ZIP entries, upload commit, and run confirmation come next.
                    const finalizing =
                      stage === "packaging" &&
                      (copiedAllItems || phase?.status === "complete");
                    const complete =
                      stage !== "packaging" &&
                      (phase?.status === "complete" || copiedAllItems);
                    const count =
                      phase?.total != null && phase.total > 0
                        ? `${phase.completed} of ${phase.total} ${progressUnits[stage] ?? "items"}`
                        : null;
                    return (
                      <div key={stage} className="flex items-start gap-3">
                        {complete ? (
                          <Check
                            className="mt-0.5 size-4 shrink-0 text-text-secondary"
                            aria-hidden="true"
                          />
                        ) : phase ? (
                          <Spinner className="mt-0.5 size-4 shrink-0" />
                        ) : (
                          <span className="mt-1 size-3 rounded-full border border-border-subtle" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex justify-between gap-2 body-sm">
                            <span>
                              {finalizing ? "Finalizing download…" : label}
                            </span>
                            <span className="text-text-secondary">
                              {complete
                                ? "Done"
                                : phase
                                  ? "Working"
                                  : "Waiting"}
                            </span>
                          </div>
                          {phase &&
                            !complete &&
                            !finalizing &&
                            phase.total != null &&
                            phase.total > 0 && (
                              <Progress
                                value={(phase.completed / phase.total) * 100}
                                aria-label={label}
                                aria-valuetext={count ?? undefined}
                                className="mt-2 h-1"
                              />
                            )}
                          {stage === "packaging" &&
                            !finalizing &&
                            phase?.bytes != null && (
                              <p className="body-sm text-text-secondary tabular-nums">
                                {(phase.bytes / 1024 ** 3).toFixed(2)} GB copied
                              </p>
                            )}
                          {finalizing && (
                            <p className="body-sm text-text-secondary">
                              Finishing the ZIP and upload. This can take a few
                              minutes.
                            </p>
                          )}
                          {count && !complete && !finalizing && (
                            <p className="body-sm text-text-secondary tabular-nums">
                              {count}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                <p className="body-sm text-text-secondary">
                  Temporary download failures are retried automatically. You can
                  keep reviewing while your export prepares.
                </p>
              </div>
            )}

            {pending.status === "unknown" && (
              <p>
                Connection lost. Preparation may still be running. Reopen this
                dialog to check before retrying.
              </p>
            )}
            {!busy && data?.status === "CANCELED" && (
              <p>
                Export canceled. You can prepare a new snapshot whenever you’re
                ready.
              </p>
            )}
            {!busy && data?.status === "FAILED" && (
              <p className="text-destructive">{data.error}</p>
            )}
            {!busy && data?.needsSnapshotRefresh && (
              <p className="body-sm text-text-secondary">
                Prepare a new snapshot to include all application attachments in
                one ZIP.
              </p>
            )}
            {ready && (
              <div className="space-y-4">
                <div className="space-y-1">
                  <p className="text-text-secondary">{data.roundName}</p>
                  <p className="body-sm text-text-secondary">
                    Snapshot prepared{" "}
                    {new Date(data.requestedAt).toLocaleString(undefined, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </p>
                </div>
                <Button
                  variant="flat"
                  className="min-h-11 w-full whitespace-normal"
                  onClick={() => void download(mediaOf ? "media" : "package")}
                >
                  <Download className="size-4 shrink-0" aria-hidden="true" />
                  {mediaOf ? "Download attachments ZIP" : "Download round ZIP"}
                </Button>
                {!mediaOf && (
                  <p className="body-sm text-text-secondary">
                    Applications, available application attachments, notes,
                    research, ratings and funding data.
                  </p>
                )}
              </div>
            )}
          </div>
          {!mediaOf && (
            <RoundVideoDownload
              roundId={roundId}
              viewerId={viewerId}
              withDetails
            />
          )}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border-subtle pt-4 [&>button]:min-h-11 [&>button]:whitespace-normal">
            {busy ? (
              <>
                <Button
                  variant="quiet"
                  className="mr-auto"
                  disabled={cancel.isPending}
                  onClick={() => cancel.run()}
                >
                  {cancel.isPending ? "Canceling…" : "Cancel export"}
                </Button>
                <Button variant="flat" onClick={() => setOpen(false)}>
                  Continue reviewing
                </Button>
              </>
            ) : (
              <>
                {ready ? (
                  <>
                    <Button variant="quiet" onClick={() => pending.run()}>
                      <RefreshCw className="size-4" aria-hidden="true" />
                      {mediaOf ? "Prepare again" : "Prepare new snapshot"}
                    </Button>
                    <Button variant="quiet" onClick={() => setOpen(false)}>
                      Done
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="quiet" onClick={() => setOpen(false)}>
                      Close
                    </Button>
                    <Button
                      variant="flat"
                      disabled={query.isLoading || query.isError}
                      onClick={() => pending.run()}
                    >
                      {mediaOf ? "Prepare attachments ZIP" : "Prepare export"}
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
